import { useEffect, useState } from "react";
import { Plus, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, TableWrap, StatusBadge, th, td, tr } from "@/components/common";

const EMPTY = { name: "", email: "", password: "", tenant_role: "manager", driver_id: "" };

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [linkOpen, setLinkOpen] = useState(false);
  const [lf, setLf] = useState({ email: "", tenant_role: "operation", driver_id: "" });
  const link = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users/memberships", { ...lf, driver_id: lf.tenant_role === "driver" ? lf.driver_id : null });
      toast.success("User added to this company");
      setLinkOpen(false);
      setLf({ email: "", tenant_role: "operation", driver_id: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const load = () => { api.get("/users").then((r) => setUsers(r.data)); api.get("/drivers").then((r) => setDrivers(r.data)); };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    try {
      await api.post("/users", { ...f, driver_id: f.tenant_role === "driver" ? f.driver_id : null });
      toast.success("User created");
      setOpen(false);
      setF(EMPTY);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (u, body, msg) => {
    try { await api.patch(u.membership_id ? `/users/memberships/${u.membership_id}` : `/users/${u.id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reset = (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars)`);
    if (pw && pw.length >= 6) patch(u, { password: pw }, "Password updated");
  };
  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.name} (${u.email})? This cannot be undone.`)) return;
    try { await api.delete(u.membership_id ? `/users/memberships/${u.membership_id}` : `/users/${u.id}`); toast.success(u.membership_id ? "Company access removed" : "User deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const freeDrivers = drivers.filter((d) => !d.user_id);

  return (
    <div>
      <PageHeader title="Team & Access" subtitle="Create logins for managers, dispatchers and drivers">
        <Button variant="outline" data-testid="link-user-btn" onClick={() => setLinkOpen(true)}>Add Existing User</Button>
        <Button data-testid="add-user-btn" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" />Add User</Button>
      </PageHeader>
      <TableWrap>
        <thead><tr>{["Name", "Email", "Role", "Status", "Active", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className={tr} data-testid={`user-row-${u.email}`}>
              <td className={`${td} font-semibold`}>{u.name}</td>
              <td className={td}>{u.email}</td>
              <td className={td}>
                <select data-testid={`user-role-${u.email}`} disabled={u.id === user.id} className="native-select w-32" value={u.tenant_role} onChange={(e) => patch(u, { tenant_role: e.target.value }, "Role updated")}>
                  <option value="admin">Admin</option><option value="manager">Manager</option><option value="operation">Operation Team</option><option value="accounts">Accounts Team</option>{u.driver_id && <option value="driver">Driver</option>}
                </select>
              </td>
              <td className={td}><StatusBadge status={u.active ? "Active" : "Disabled"} /></td>
              <td className={td}><Switch data-testid={`user-active-${u.email}`} disabled={u.id === user.id} checked={u.active} onCheckedChange={(v) => patch(u, { active: v }, v ? "User enabled" : "User disabled")} /></td>
              <td className={`${td} text-right`}>
                {u.membership_id ? <span className="mr-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700 dark:bg-sky-950 dark:text-sky-300" data-testid={`shared-badge-${u.email}`}>Shared</span>
                  : <button data-testid={`reset-password-${u.email}`} onClick={() => reset(u)} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Reset password"><KeyRound className="h-4 w-4" /></button>}
                {u.id !== user.id && <button data-testid={`delete-user-${u.email}`} onClick={() => del(u)} className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40" title="Delete user"><Trash2 className="h-4 w-4" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-card" data-testid="user-dialog">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Role">
              <select data-testid="new-user-role-select" className="native-select" value={f.tenant_role} onChange={set("tenant_role")}>
                <option value="manager">Manager / Dispatcher</option><option value="operation">Operation Team</option><option value="accounts">Accounts Team</option><option value="driver">Driver</option><option value="admin">Admin / Owner</option>
              </select>
            </Field>
            {f.tenant_role === "driver" && (
              <Field label="Link Driver Profile">
                <select data-testid="new-user-driver-select" className="native-select" value={f.driver_id} onChange={(e) => { const d = drivers.find((x) => x.id === e.target.value); setF({ ...f, driver_id: e.target.value, name: d?.name || f.name }); }}>
                  <option value="">Select driver…</option>
                  {freeDrivers.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>)}
                </select>
              </Field>
            )}
            <Field label="Name"><Input data-testid="new-user-name-input" value={f.name} onChange={set("name")} /></Field>
            <Field label="Email"><Input data-testid="new-user-email-input" type="email" value={f.email} onChange={set("email")} /></Field>
            <Field label="Password"><Input data-testid="new-user-password-input" type="text" value={f.password} onChange={set("password")} placeholder="Min 6 characters" /></Field>
          </div>
          <DialogFooter><Button data-testid="new-user-save-btn" onClick={create}>Create Login</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent data-testid="link-user-dialog">
          <DialogHeader><DialogTitle>Add Existing User to this Company</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Doosri company ka koi bhi existing user is company me alag role ke saath access paa sakta hai. Wo header switcher se company badal sakega.</p>
          <form onSubmit={link} className="space-y-3">
            <input data-testid="link-user-email-input" type="email" required placeholder="User email" value={lf.email} onChange={(e) => setLf({ ...lf, email: e.target.value })} className="native-select w-full" />
            <select data-testid="link-user-role-select" className="native-select" value={lf.tenant_role} onChange={(e) => setLf({ ...lf, tenant_role: e.target.value })}>
              <option value="manager">Manager</option><option value="operation">Operation Team</option><option value="accounts">Accounts Team</option><option value="driver">Driver</option><option value="admin">Admin</option>
            </select>
            {lf.tenant_role === "driver" && (
              <select data-testid="link-user-driver-select" className="native-select" required value={lf.driver_id} onChange={(e) => setLf({ ...lf, driver_id: e.target.value })}>
                <option value="">Link driver profile…</option>
                {drivers.filter((d) => !d.user_id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="link-user-save-btn">Give Access</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
